import React, { useEffect, useState } from "react";
import { FileText, Download, PenLine, CheckCircle2, ScrollText, Receipt } from "lucide-react";
import { toast } from "sonner";
import api, { API, apiErrorMessage } from "@/lib/api";
import { CURRENCY } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LanguageContext";

const C = {
  fr: { title: "Factures & Contrats", subtitle: "Retrouvez vos documents contractuels et vos factures téléchargeables.",
    myContracts: "Mes contrats", contractsHere: "Vos contrats apparaîtront ici après la validation d'un paiement.",
    contract: "Contrat", signedBy: "Signé par", on: "le", awaiting: "En attente de signature", sign: "Signer",
    myInvoices: "Mes factures", invoicesHere: "Vos factures apparaîtront ici après la validation d'un paiement.",
    signTitle: "Signature électronique", signIntro1: "En signant, vous acceptez les termes du contrat de prestation",
    signIntro2: "Pour confirmer, saisissez votre nom complet exactement tel qu'enregistré :", fullName: "Nom complet",
    signBtn: "Signer le contrat", signing: "Signature…", signOk: "Contrat signé électroniquement !",
    signNote: "Cette signature électronique a valeur d'engagement. Un service de signature avancé (ex. DocuSign) pourra être ajouté ultérieurement." },
  en: { title: "Invoices & Contracts", subtitle: "Find your contractual documents and downloadable invoices.",
    myContracts: "My contracts", contractsHere: "Your contracts will appear here after a payment is validated.",
    contract: "Contract", signedBy: "Signed by", on: "on", awaiting: "Awaiting signature", sign: "Sign",
    myInvoices: "My invoices", invoicesHere: "Your invoices will appear here after a payment is validated.",
    signTitle: "Electronic signature", signIntro1: "By signing, you accept the terms of the service contract",
    signIntro2: "To confirm, enter your full name exactly as registered:", fullName: "Full name",
    signBtn: "Sign the contract", signing: "Signing…", signOk: "Contract signed electronically!",
    signNote: "This electronic signature is legally binding. An advanced signature service (e.g. DocuSign) may be added later." },
};

export default function Facturation() {
  const { lang } = useLang();
  const c = C[lang];
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [signing, setSigning] = useState(null);
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/invoices/me").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/contracts/me").then((r) => setContracts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const token = localStorage.getItem("wte_token");
  const openPdf = (kind, id) => window.open(`${API}/${kind}/${id}/pdf?auth=${token}`, "_blank");

  const doSign = async () => {
    setBusy(true);
    try {
      await api.post(`/contracts/${signing.id}/sign`, { full_name: fullName });
      toast.success(c.signOk);
      setSigning(null); setFullName(""); load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="facturation-page">
      <h1 className="font-heading text-3xl sm:text-4xl text-navy">{c.title}</h1>
      <p className="text-muted-foreground mt-2">{c.subtitle}</p>

      <div className="mt-8">
        <h2 className="flex items-center gap-2 font-heading text-2xl text-navy"><ScrollText size={22} className="text-gold" /> {c.myContracts}</h2>
        {contracts.length === 0 ? (
          <div className="bg-white border border-border p-8 text-center mt-4 text-muted-foreground text-sm">{c.contractsHere}</div>
        ) : (
          <div className="bg-white border border-border divide-y divide-border mt-4">
            {contracts.map((ct) => (
              <div key={ct.id} data-testid={`contract-${ct.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center shrink-0"><FileText size={18} /></div>
                  <div className="min-w-0">
                    <p className="text-navy font-medium truncate">{c.contract} — {ct.pack_name}</p>
                    {ct.signed
                      ? <p className="text-xs text-green-700 flex items-center gap-1 mt-0.5"><CheckCircle2 size={13} /> {c.signedBy} {ct.signed_name} {c.on} {ct.signed_at?.slice(0,10)}</p>
                      : <p className="text-xs text-amber-600 mt-0.5">{c.awaiting}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!ct.signed && (
                    <button data-testid={`sign-btn-${ct.id}`} onClick={() => { setSigning(ct); setFullName(""); }}
                      className="flex items-center gap-2 bg-gold hover:bg-gold-hover text-white px-4 py-2 text-sm transition-colors"><PenLine size={15} /> {c.sign}</button>
                  )}
                  <button data-testid={`contract-pdf-${ct.id}`} onClick={() => openPdf("contracts", ct.id)}
                    className="flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-4 py-2 text-sm transition-colors"><Download size={15} /> PDF</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10">
        <h2 className="flex items-center gap-2 font-heading text-2xl text-navy"><Receipt size={22} className="text-gold" /> {c.myInvoices}</h2>
        {invoices.length === 0 ? (
          <div className="bg-white border border-border p-8 text-center mt-4 text-muted-foreground text-sm">{c.invoicesHere}</div>
        ) : (
          <div className="bg-white border border-border divide-y divide-border mt-4">
            {invoices.map((inv) => (
              <div key={inv.id} data-testid={`invoice-${inv.id}`} className="flex items-center justify-between gap-3 p-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center shrink-0"><Receipt size={18} /></div>
                  <div className="min-w-0">
                    <p className="text-navy font-medium truncate">{inv.number}</p>
                    <p className="text-xs text-muted-foreground">{inv.pack_name} · {inv.created_at?.slice(0,10)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-navy font-medium">{CURRENCY(inv.amount)}</span>
                  <button data-testid={`invoice-pdf-${inv.id}`} onClick={() => openPdf("invoices", inv.id)}
                    className="flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-4 py-2 text-sm transition-colors"><Download size={15} /> PDF</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!signing} onOpenChange={(o) => !o && setSigning(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-navy">{c.signTitle}</DialogTitle></DialogHeader>
          {signing && (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">
                {c.signIntro1} « {signing.pack_name} ». {c.signIntro2} <strong>{signing.user_name}</strong>.
              </p>
              <div>
                <Label className="text-navy">{c.fullName}</Label>
                <Input data-testid="sign-fullname" value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-2 rounded-none" placeholder={signing.user_name} />
              </div>
              <button data-testid="sign-confirm" disabled={busy || !fullName} onClick={doSign}
                className="w-full bg-gold hover:bg-gold-hover text-white py-3 font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                <PenLine size={16} /> {busy ? c.signing : c.signBtn}
              </button>
              <p className="text-xs text-muted-foreground">{c.signNote}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
